import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Tooltip,
  CircularProgress,
  Collapse,
  Divider,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Loop as RunningIcon,
  Cancel as CancelIcon,
  OpenInNew as OpenIcon,
  Delete as ClearIcon,
  Pause as PauseIcon,
  Assessment as StatsIcon,
  CleaningServices as CleanupIcon,
  Storage as PoolIcon,
  Queue as QueueIcon,
} from '@mui/icons-material';
import GoldIngotLoader from './GoldIngotLoader';

// Flag icons for markets
const FlagUS = () => (
  <Box component="span" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>🇺🇸</Box>
);
const FlagUK = () => (
  <Box component="span" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>🇬🇧</Box>
);
const FlagJP = () => (
  <Box component="span" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>🇯🇵</Box>
);
const FlagCA = () => (
  <Box component="span" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>🇨🇦</Box>
);

const MARKET_FLAGS = {
  US: FlagUS,
  UK: FlagUK,
  JP: FlagJP,
  CA: FlagCA,
};

// Status icons and colors
const STATUS_CONFIG = {
  pending: { icon: PendingIcon, color: 'default', label: 'Pending' },
  running: { icon: RunningIcon, color: 'info', label: 'Running' },
  success: { icon: SuccessIcon, color: 'success', label: 'Success' },
  completed: { icon: SuccessIcon, color: 'success', label: 'Completed' },
  failed: { icon: ErrorIcon, color: 'error', label: 'Failed' },
  stopped: { icon: CancelIcon, color: 'warning', label: 'Stopped' },
  skipped: { icon: CancelIcon, color: 'default', label: 'Skipped' },
  cancelled: { icon: CancelIcon, color: 'default', label: 'Cancelled' },
};

function StatusChip({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: 16 }} />}
      label={config.label}
      color={config.color}
      size="small"
      sx={{ minWidth: 90 }}
    />
  );
}

function formatDuration(seconds) {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

function formatTime(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function ScraperMonitor() {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runningExecution, setRunningExecution] = useState(null);
  const [categoriesStatus, setCategoriesStatus] = useState({ byMarket: {}, marketStats: {} });
  const [selectedMarket, setSelectedMarket] = useState('ALL');
  const [selectedStage, setSelectedStage] = useState('full');
  const [statusFilter, setStatusFilter] = useState('all');
  const [logs, setLogs] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [categoryHistory, setCategoryHistory] = useState({});
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);

  // Phase 4-5 State
  const [activeTab, setActiveTab] = useState(0);
  const [poolStatus, setPoolStatus] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [failures, setFailures] = useState([]);

  const logsEndRef = useRef(null);
  const eventSourceRef = useRef(null);

  // Fetch initial data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch categories status
      const catRes = await fetch('/api/scraper-monitor/categories/today');
      const catData = await catRes.json();
      if (catData.success) {
        setCategoriesStatus(catData.data);
      }

      // Fetch running execution
      const runRes = await fetch('/api/scraper-monitor/executions/running');
      const runData = await runRes.json();
      if (runData.success) {
        setRunningExecution(runData.data);
      }

      // Fetch pool and queue status (Phase 4-5)
      try {
        const poolRes = await fetch('/api/scraper-monitor/pool/status');
        const poolData = await poolRes.json();
        if (poolData.success) setPoolStatus(poolData.data);
      } catch (e) { /* ignore */ }

      try {
        const queueRes = await fetch('/api/scraper-monitor/queue/status');
        const queueData = await queueRes.json();
        if (queueData.success) setQueueStatus(queueData.data);
      } catch (e) { /* ignore */ }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch statistics (Phase 5)
  const fetchStatistics = useCallback(async () => {
    try {
      const statsRes = await fetch('/api/scraper-monitor/statistics?days=7');
      const statsData = await statsRes.json();
      if (statsData.success) setStatistics(statsData.data);

      const failRes = await fetch('/api/scraper-monitor/failures?limit=10');
      const failData = await failRes.json();
      if (failData.success) setFailures(failData.data);
    } catch (e) {
      console.error('Error fetching statistics:', e);
    }
  }, []);

  // Setup SSE connection
  useEffect(() => {
    fetchData();
    fetchStatistics();

    // Connect to SSE
    const eventSource = new EventSource('/api/scraper-monitor/events');
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'status_update') {
          setRunningExecution(data.execution);
          // Refresh categories when status updates
          fetchData();
        } else if (data.type === 'log') {
          setLogs(prev => [...prev.slice(-99), data]);
        }
      } catch (err) {
        console.error('Error parsing SSE message:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('SSE error:', err);
    };

    return () => {
      eventSource.close();
    };
  }, [fetchData, fetchStatistics]);

  // Auto-scroll logs
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  // Start scraper
  const handleStart = async () => {
    try {
      setStarting(true);
      const body = {
        stage2_only: selectedStage === 'stage2',
      };
      if (selectedMarket !== 'ALL') {
        body.market_code = selectedMarket;
      }

      const res = await fetch('/api/scraper-monitor/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (!data.success) {
        setError(data.error);
      } else {
        setLogs(prev => [...prev, {
          type: 'log',
          timestamp: new Date().toISOString(),
          message: `Started scraper (PID: ${data.pid})`,
          level: 'info',
        }]);
        fetchData();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setStarting(false);
    }
  };

  // Stop scraper
  const handleStop = async () => {
    try {
      setStopping(true);
      const res = await fetch('/api/scraper-monitor/stop', {
        method: 'POST',
      });
      const data = await res.json();

      if (!data.success) {
        setError(data.error);
      } else {
        setLogs(prev => [...prev, {
          type: 'log',
          timestamp: new Date().toISOString(),
          message: 'Stop signal sent',
          level: 'warning',
        }]);
        fetchData();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setStopping(false);
    }
  };

  // Retry category
  const handleRetry = async (categoryId) => {
    try {
      const res = await fetch(`/api/scraper-monitor/retry/${categoryId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage2_only: selectedStage === 'stage2' }),
      });
      const data = await res.json();

      if (!data.success) {
        setError(data.error);
      } else {
        fetchData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch category history
  const handleExpandCategory = async (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
      return;
    }

    setExpandedCategory(categoryId);

    if (!categoryHistory[categoryId]) {
      try {
        const res = await fetch(`/api/scraper-monitor/categories/${categoryId}/history`);
        const data = await res.json();
        if (data.success) {
          setCategoryHistory(prev => ({ ...prev, [categoryId]: data.data }));
        }
      } catch (err) {
        console.error('Error fetching category history:', err);
      }
    }
  };

  // Filter categories
  const getFilteredCategories = (market) => {
    const cats = categoriesStatus.byMarket[market] || [];
    if (statusFilter === 'all') return cats;

    return cats.filter(cat => {
      if (statusFilter === 'pending') return !cat.today_status || cat.today_status === 'pending';
      if (statusFilter === 'running') return cat.today_status === 'running';
      if (statusFilter === 'success') return cat.today_status === 'success';
      if (statusFilter === 'failed') return cat.today_status === 'failed';
      return true;
    });
  };

  const markets = Object.keys(categoriesStatus.byMarket);

  // Handle cleanup (Phase 5)
  const handleCleanup = async (days = 30) => {
    if (!window.confirm(`Delete execution records older than ${days} days?`)) return;
    try {
      const res = await fetch('/api/scraper-monitor/cleanup/executions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days }),
      });
      const data = await res.json();
      if (data.success) {
        setLogs(prev => [...prev, {
          type: 'log',
          timestamp: new Date().toISOString(),
          message: `Cleanup complete: ${data.data.executionsDeleted} executions, ${data.data.logsDeleted} logs deleted`,
          level: 'info',
        }]);
        fetchStatistics();
      }
    } catch (e) {
      setError(e.message);
    }
  };

  // Render Statistics Tab (Phase 5)
  const renderStatisticsTab = () => (
    <Box sx={{ mt: 2 }}>
      {statistics ? (
        <Grid container spacing={2}>
          {/* Summary Cards */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              7-Day Summary
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Total Executions</Typography>
                <Typography variant="h4">{statistics.summary?.total_executions || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Success Rate</Typography>
                <Typography variant="h4" color="success.main">
                  {statistics.summary?.success_rate_percent || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Failed</Typography>
                <Typography variant="h4" color="error.main">
                  {statistics.summary?.failed_executions || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Avg Duration</Typography>
                <Typography variant="h4">{statistics.summary?.avg_duration_minutes || 0}m</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* By Market */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>By Market</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Market</TableCell>
                      <TableCell align="right">Executions</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">Failed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(statistics.byMarket || []).map((m, i) => (
                      <TableRow key={i}>
                        <TableCell>{m.market_code}</TableCell>
                        <TableCell align="right">{m.total_executions}</TableCell>
                        <TableCell align="right">{m.completed}</TableCell>
                        <TableCell align="right">{m.failed}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Recent Failures */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent Failures</Typography>
              {failures.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No recent failures</Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell>Market</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failures.slice(0, 5).map((f, i) => (
                        <TableRow key={i}>
                          <TableCell>{f.category_name}</TableCell>
                          <TableCell>{f.market_code}</TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {f.error_message?.substring(0, 50)}...
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Cleanup Button */}
          <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={<CleanupIcon />}
              onClick={() => handleCleanup(30)}
              size="small"
            >
              Cleanup Old Data (30+ days)
            </Button>
          </Grid>
        </Grid>
      ) : (
        <GoldIngotLoader minHeight={300} />
      )}
    </Box>
  );

  // Render System Status (Phase 4)
  const renderSystemStatus = () => (
    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
      {/* Pool Status */}
      {poolStatus?.available && (
        <Chip
          icon={<PoolIcon />}
          label={`Pages: ${poolStatus.inUse || 0}/${poolStatus.maxConcurrent || 3} in use`}
          color={poolStatus.inUse > 0 ? 'info' : 'default'}
          variant="outlined"
          size="small"
        />
      )}
      {/* Queue Status */}
      {queueStatus?.available && (
        <Chip
          icon={<QueueIcon />}
          label={`Queue: ${queueStatus.runningCount || 0} running, ${queueStatus.queueLength || 0} waiting`}
          color={queueStatus.runningCount > 0 ? 'warning' : 'default'}
          variant="outlined"
          size="small"
        />
      )}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RunningIcon color="primary" /> Scraper Monitor
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="Monitor" />
            <Tab label="Statistics" icon={<StatsIcon fontSize="small" />} iconPosition="start" />
          </Tabs>
        </Box>
      </Box>

      {/* System Status (Phase 4) */}
      {renderSystemStatus()}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Tab */}
      {activeTab === 1 && renderStatisticsTab()}

      {/* Monitor Tab */}
      {activeTab === 0 && (
      <>
      {/* Execution Control */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Execution Control
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Market Selection */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Market</Typography>
            <ToggleButtonGroup
              value={selectedMarket}
              exclusive
              onChange={(e, v) => v && setSelectedMarket(v)}
              size="small"
            >
              <ToggleButton value="ALL">All</ToggleButton>
              <ToggleButton value="US"><FlagUS /> US</ToggleButton>
              <ToggleButton value="UK"><FlagUK /> UK</ToggleButton>
              <ToggleButton value="JP"><FlagJP /> JP</ToggleButton>
              <ToggleButton value="CA"><FlagCA /> CA</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Stage Selection */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Stage</Typography>
            <FormControl>
              <RadioGroup
                row
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
              >
                <FormControlLabel value="full" control={<Radio size="small" />} label="Full" />
                <FormControlLabel value="stage1" control={<Radio size="small" />} label="Stage 1 Only" />
                <FormControlLabel value="stage2" control={<Radio size="small" />} label="Stage 2 Only" />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={starting ? <CircularProgress size={16} /> : <PlayIcon />}
              onClick={handleStart}
              disabled={starting || !!runningExecution}
            >
              Start
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={stopping ? <CircularProgress size={16} /> : <StopIcon />}
              onClick={handleStop}
              disabled={stopping || !runningExecution}
            >
              Stop
            </Button>
            <IconButton onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Running Execution Status */}
      {runningExecution && (
        <Paper sx={{ p: 2, mb: 3, borderLeft: 4, borderColor: 'info.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <RunningIcon color="info" sx={{ animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Execution #{runningExecution.id}
            </Typography>
            <Chip label={runningExecution.stage?.toUpperCase()} size="small" color="primary" />
            {runningExecution.market_code && (
              <Chip label={runningExecution.market_code} size="small" />
            )}
            <Box sx={{ ml: 'auto' }}>
              <Typography variant="body2" color="text.secondary">
                Started: {formatTime(runningExecution.started_at)}
              </Typography>
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                {runningExecution.current_category_name || 'Starting...'}
                {runningExecution.current_progress && ` (${runningExecution.current_progress})`}
              </Typography>
              <Typography variant="body2">
                {runningExecution.completed_categories || 0} / {runningExecution.total_categories || 0} categories
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={runningExecution.total_categories ? (runningExecution.completed_categories / runningExecution.total_categories) * 100 : 0}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          {/* Stats */}
          <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SuccessIcon color="success" fontSize="small" />
              <Typography variant="body2">{runningExecution.success_count || 0} Success</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ErrorIcon color="error" fontSize="small" />
              <Typography variant="body2">{runningExecution.failed_count || 0} Failed</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Categories Status */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Categories Status
          </Typography>

          {/* Status Filter */}
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(e, v) => v && setStatusFilter(v)}
            size="small"
            sx={{ ml: 'auto' }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="pending">Pending</ToggleButton>
            <ToggleButton value="running">Running</ToggleButton>
            <ToggleButton value="success">Success</ToggleButton>
            <ToggleButton value="failed">Failed</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {loading ? (
          <GoldIngotLoader minHeight={300} />
        ) : (
          markets.map((market) => {
            const FlagIcon = MARKET_FLAGS[market];
            const stats = categoriesStatus.marketStats[market] || {};
            const categories = getFilteredCategories(market);

            return (
              <Accordion key={market} defaultExpanded={market === 'US'}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    {FlagIcon && <FlagIcon />}
                    <Typography fontWeight="bold">{market}</Typography>
                    <Typography color="text.secondary">({stats.total || 0} categories)</Typography>
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                      <Chip icon={<SuccessIcon />} label={stats.success || 0} color="success" size="small" />
                      <Chip icon={<ErrorIcon />} label={stats.failed || 0} color="error" size="small" />
                      <Chip icon={<PendingIcon />} label={stats.pending || 0} size="small" />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={50}>#</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell width={80}>Limit</TableCell>
                          <TableCell width={120}>Last Run</TableCell>
                          <TableCell width={100}>Status</TableCell>
                          <TableCell width={80}>Products</TableCell>
                          <TableCell width={100}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categories.map((cat, idx) => (
                          <React.Fragment key={cat.id}>
                            <TableRow
                              hover
                              onClick={() => handleExpandCategory(cat.id)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>{cat.sequence || idx + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2">{cat.name}</Typography>
                                {cat.name_zh && (
                                  <Typography variant="caption" color="text.secondary">{cat.name_zh}</Typography>
                                )}
                              </TableCell>
                              <TableCell>{cat.products_limit}</TableCell>
                              <TableCell>
                                {cat.started_at ? formatTime(cat.started_at) : '-'}
                              </TableCell>
                              <TableCell>
                                <StatusChip status={cat.today_status || 'pending'} />
                              </TableCell>
                              <TableCell>{cat.products_saved || '-'}</TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                {cat.today_status === 'failed' ? (
                                  <Tooltip title="Retry this category">
                                    <IconButton size="small" onClick={() => handleRetry(cat.scraper_category_id)}>
                                      <RefreshIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Run this category">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRetry(cat.scraper_category_id)}
                                      disabled={!!runningExecution}
                                    >
                                      <PlayIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {cat.amazon_url && (
                                  <Tooltip title="Open Amazon page">
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={cat.amazon_url}
                                      target="_blank"
                                    >
                                      <OpenIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>

                            {/* Expanded Details */}
                            <TableRow>
                              <TableCell colSpan={7} sx={{ p: 0 }}>
                                <Collapse in={expandedCategory === cat.id}>
                                  <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                    {/* Category Info */}
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Category Info</Typography>
                                    <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                                      <Typography variant="body2">ID: {cat.id}</Typography>
                                      <Typography variant="body2">Postal Code: {cat.postal_code || '-'}</Typography>
                                    </Box>

                                    {/* Error Message */}
                                    {cat.error_message && (
                                      <Alert severity="error" sx={{ mb: 2 }}>
                                        {cat.error_message}
                                      </Alert>
                                    )}

                                    {/* History */}
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>History (7 days)</Typography>
                                    {categoryHistory[cat.id] ? (
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Stage</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Products</TableCell>
                                            <TableCell>Duration</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {categoryHistory[cat.id].map((h, i) => (
                                            <TableRow key={i}>
                                              <TableCell>{h.date}</TableCell>
                                              <TableCell>{h.item_stage}</TableCell>
                                              <TableCell><StatusChip status={h.status} /></TableCell>
                                              <TableCell>{h.products_saved || 0}</TableCell>
                                              <TableCell>{formatDuration(h.duration_seconds)}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    ) : (
                                      <CircularProgress size={20} />
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Paper>

      {/* Real-time Logs */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Real-time Logs
          </Typography>
          <IconButton size="small" onClick={() => setLogs([])} sx={{ ml: 'auto' }}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            height: 200,
            overflow: 'auto',
            bgcolor: 'grey.900',
            color: 'grey.100',
            p: 1,
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
          }}
        >
          {logs.length === 0 ? (
            <Typography color="grey.500" sx={{ fontStyle: 'italic' }}>
              No logs yet. Start a scraper to see real-time updates.
            </Typography>
          ) : (
            logs.map((log, i) => (
              <Box key={i} sx={{ mb: 0.5 }}>
                <span style={{ color: '#888' }}>{formatTime(log.timestamp)}</span>
                {' '}
                <span style={{ color: log.level === 'error' ? '#f44336' : log.level === 'warning' ? '#ff9800' : '#4caf50' }}>
                  [{log.level?.toUpperCase() || 'INFO'}]
                </span>
                {' '}
                {log.message}
              </Box>
            ))
          )}
          <div ref={logsEndRef} />
        </Box>
      </Paper>
      </>
      )}
    </Box>
  );
}

export default ScraperMonitor;
